import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { roomService } from '../services/roomService';
import toast from 'react-hot-toast';

const { FiPlus, FiCopy, FiCheck, FiLoader, FiSmartphone } = FiIcons;

const RoomCreation = ({ onRoomCreated }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [createdRoom, setCreatedRoom] = useState(null);
  const [copied, setCopied] = useState({ name: false, password: false });

  const handleCreateRoom = async () => {
    setIsCreating(true);
    try {
      const result = await roomService.createRoom();
      if (result.success) {
        setCreatedRoom(result.room);
        toast.success('Room created successfully!');
      } else {
        toast.error(result.error || 'Failed to create room');
      }
    } catch (error) {
      toast.error('Failed to create room');
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      toast.success(`${type === 'name' ? 'Room name' : 'Password'} copied!`);
      setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const shareUrl = createdRoom 
    ? `${window.location.origin}${window.location.pathname}?room=${encodeURIComponent(createdRoom.name)}&pass=${encodeURIComponent(createdRoom.password)}`
    : '';

  if (createdRoom) {
    return (
      <motion.div 
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto border border-gray-100"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="text-center mb-6">
          <motion.div 
            className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12 }}
          >
            <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
          </motion.div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Room Created!</h3>
          <p className="text-gray-600">Share or scan the code below</p>
        </div>

        <div className="bg-gray-50 rounded-2xl p-6 mb-6 flex flex-col items-center border border-gray-100">
          <div className="bg-white p-4 rounded-xl shadow-sm mb-4">
            <QRCodeCanvas 
              value={shareUrl} 
              size={160}
              level="H"
              includeMargin={false}
              imageSettings={{
                src: "/vite.svg",
                x: undefined,
                y: undefined,
                height: 24,
                width: 24,
                excavate: true,
              }}
            />
          </div>
          <div className="flex items-center gap-2 text-blue-600 font-bold text-sm">
            <SafeIcon icon={FiSmartphone} className="w-4 h-4" />
            Scan to join on mobile
          </div>
        </div>

        <div className="space-y-4">
          <CredentialRow 
            label="Room Name" 
            value={createdRoom.name} 
            onCopy={() => copyToClipboard(createdRoom.name, 'name')}
            isCopied={copied.name}
          />
          <CredentialRow 
            label="Password" 
            value={createdRoom.password} 
            onCopy={() => copyToClipboard(createdRoom.password, 'password')}
            isCopied={copied.password}
          />
        </div>

        <div className="mt-8 space-y-3">
          <motion.button
            onClick={() => onRoomCreated(createdRoom.name, createdRoom.password)}
            className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold shadow-lg"
            whileHover={{ scale: 1.02, backgroundColor: '#1d4ed8' }}
            whileTap={{ scale: 0.98 }}
          >
            Join This Room
          </motion.button>
          <button 
            onClick={() => setCreatedRoom(null)}
            className="w-full text-gray-500 py-2 text-sm font-medium hover:text-gray-700 transition-colors"
          >
            Create another one
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiPlus} className="w-8 h-8 text-primary-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Create New Room</h3>
        <p className="text-gray-600">Secure, temporary room for instant sharing</p>
      </div>

      <motion.button
        onClick={handleCreateRoom}
        disabled={isCreating}
        className="w-full bg-primary-600 text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 group"
        whileHover={{ scale: 1.02, backgroundColor: '#1d4ed8' }}
        whileTap={{ scale: 0.98 }}
      >
        {isCreating ? (
          <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <motion.span variants={{ hover: { rotate: 90 } }} transition={{ duration: 0.2 }}>
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
            </motion.span>
            Create Room
          </>
        )}
      </motion.button>

      <div className="mt-8 pt-6 border-t border-gray-100 space-y-4 text-sm">
        <div className="flex items-start gap-3 text-blue-800 bg-blue-50/50 p-3 rounded-lg">
          <SafeIcon icon={FiCheck} className="w-4 h-4 mt-0.5 text-blue-600" />
          <span>QR code for instant mobile joining</span>
        </div>
        <div className="flex items-start gap-3 text-amber-800 bg-amber-50/50 p-3 rounded-lg">
          <SafeIcon icon={FiCheck} className="w-4 h-4 mt-0.5 text-amber-600" />
          <span>Auto-delete after 1 hour of inactivity</span>
        </div>
      </div>
    </motion.div>
  );
};

const CredentialRow = ({ label, value, onCopy, isCopied }) => (
  <div>
    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1.5 ml-1">{label}</label>
    <div className="flex items-center gap-2">
      <div className="flex-1 p-3.5 bg-gray-50 rounded-xl font-mono text-sm border border-gray-100 text-gray-900 shadow-inner overflow-hidden text-ellipsis">
        {value}
      </div>
      <motion.button 
        onClick={onCopy}
        className={`p-3.5 rounded-xl transition-colors shadow-sm ${isCopied ? 'bg-green-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-primary-600 hover:text-primary-600'}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon icon={isCopied ? FiCheck : FiCopy} className="w-4 h-4" />
      </motion.button>
    </div>
  </div>
);

export default RoomCreation;